import { HABIT_COLORS, HABIT_ICONS, streakText } from '../../lib/habits.js'
import { reorder } from '../../lib/planner.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- calendar-habits-dialog ---
let __fez_component_0;
{

__fez_component_0 = class {
  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.errors = {}
    this.state.items = []
    this.state.form = null
    this.state.busy = ''
    this.state.confirming = ''
    this.state.changed = false
  }

  onMount() {
    this.load()
  }

  async load() {
    try {
      const data = await window.App.api('planner/habits', { archived: true }, { silent: true })
      this.state.items = data.items
      this.state.error = ''
    } catch (error) {
      this.state.error = error.message
    } finally {
      this.state.loading = false
    }
  }

  // Every write goes through here: a failure says why and reloads the list as the server has it.
  async write(path, params) {
    this.state.busy = params.habit || 'new'
    try {
      const saved = await window.App.api(path, params, { silent: true })
      this.state.changed = true
      return saved || true
    } catch (error) {
      if (error.code === 'validation_failed') this.state.errors = error.details || {}
      else window.Toast.error(error.message)
      if (error.code !== 'validation_failed') this.load()
      return null
    } finally {
      this.state.busy = ''
    }
  }

  async edit(ref) {
    const habit = this.state.items.find((item) => item.ref === ref)
    this.state.errors = {}
    this.state.confirming = ''
    this.state.form = habit ? { ref, name: habit.name, icon: habit.icon, color: habit.color } : { ref: '', name: '', icon: HABIT_ICONS[0].icon, color: this.nextColor() }
    await this.tick()
    this.root.querySelector('.hd-form input[name="name"]')?.focus()
  }

  // A new habit's color: the next of the palette after the last habit's.
  nextColor() {
    const last = this.state.items[this.state.items.length - 1]
    return last ? (last.color % HABIT_COLORS.length) + 1 : HABIT_COLORS[0]
  }

  pickIcon(icon) {
    this.state.form = { ...this.state.form, icon }
  }

  pickColor(color) {
    this.state.form = { ...this.state.form, color: Number(color) }
  }

  cancelEdit() {
    this.state.form = null
    this.state.errors = {}
  }

  async save(event) {
    event.preventDefault()
    const form = this.state.form
    const name = this.formData(event.target).name || ''
    const params = { name, icon: form.icon, color: form.color }
    const saved = form.ref ? await this.write('planner/habit_update', { habit: form.ref, ...params }) : await this.write('planner/habit_add', params)
    if (!saved) return
    this.state.form = null
    this.state.errors = {}
    await this.load()
  }

  // Up (-1) or down (1) one place among the shown habits.
  async nudge(ref, dir) {
    const active = this.state.items.filter((item) => !item.archived)
    const from = active.findIndex((item) => item.ref === ref)
    const moved = reorder(active, from, dir < 0 ? from - 1 : from + 2)
    if (!moved) return
    this.state.items = [...moved.items, ...this.state.items.filter((item) => item.archived)]
    if (await this.write('planner/habit_move', { habit: ref, before: moved.before || undefined })) await this.load()
  }

  async archive(ref, archived) {
    if (await this.write('planner/habit_update', { habit: ref, archived })) await this.load()
  }

  async remove(ref) {
    if (this.state.confirming !== ref) {
      this.state.confirming = ref
      return
    }
    this.state.confirming = ''
    if (await this.write('planner/habit_delete', { habit: ref })) await this.load()
  }

  done() {
    window.Dialog.close(this.state.changed || undefined)
  }

  beforeRender() {
    const row = (habit, index, list) => ({
      ...habit,
      streak: streakText(habit.streak),
      first: index === 0,
      last: index === list.length - 1,
      editing: this.state.form?.ref === habit.ref,
      confirming: this.state.confirming === habit.ref,
    })
    const active = this.state.items.filter((item) => !item.archived)
    const archived = this.state.items.filter((item) => item.archived)
    this.state.active = active.map(row)
    this.state.archived = archived.map(row)
    const form = this.state.form
    this.state.adding = Boolean(form && !form.ref)
    this.state.icons = HABIT_ICONS.map((entry) => ({ ...entry, on: entry.icon === form?.icon }))
    this.state.colors = HABIT_COLORS.map((color) => ({ color, on: color === form?.color }))
  }

  CSS = `:fez {
display:block;.hd{display:grid;gap:var(--space-4)}.hd-list{display:grid;gap:var(--space-1);margin:0;padding:0;list-style:none}.hd-group{margin-top:var(--space-2);color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-bold);letter-spacing:0.06em;text-transform:uppercase}.hd-row{display:flex;align-items:center;gap:var(--space-2);min-height:40px;padding:var(--space-1) var(--space-1) var(--space-1) var(--space-2);border-radius:var(--radius-md);&:hover{background:var(--color-bg-hover)}&.archived .hd-name{color:var(--color-text-muted)}}.hd-badge{display:grid;flex:none;place-items:center;width:28px;height:28px;border-radius:var(--radius-full);background:var(--tag);color:var(--color-text-inverse)}.hd-row.archived .hd-badge{opacity:0.5}.hd-text{display:grid;flex:1;min-width:0}.hd-name{overflow:hidden;font-size:var(--text-sm);font-weight:var(--weight-medium);text-overflow:ellipsis;white-space:nowrap}.hd-streak{color:var(--color-text-muted);font-size:var(--text-xs)}.hd-tools{display:inline-flex;flex:none}.hd-form{display:grid;gap:var(--space-3)}.hd-label{font-size:var(--text-sm);font-weight:var(--weight-semibold)}.hd-icons{display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:var(--space-1)}.hd-icon,.hd-color{display:grid;place-items:center;height:36px;padding:0;border:1px solid var(--color-border);border-radius:var(--radius-md);background:var(--color-bg-raised);color:var(--color-text-muted);cursor:pointer;&:hover{border-color:var(--color-border-strong);color:var(--color-text)}&:focus-visible{outline:2px solid var(--color-focus);outline-offset:1px}}.hd-icon.on{border-color:var(--tag);background:var(--tag);color:var(--color-text-inverse)}.hd-colors{display:flex;flex-wrap:wrap;gap:var(--space-2)}.hd-color{width:32px;height:32px;border-radius:var(--radius-full);background:var(--tag);color:var(--color-text-inverse);&.on{outline:2px solid var(--tag);outline-offset:2px}}.hd-form-actions{display:flex;justify-content:flex-end;gap:var(--space-2)}.hd-note{display:grid;place-items:center;min-height:120px}.hd-empty{color:var(--color-text-muted);font-size:var(--text-sm)} :mobile {.hd-icons{grid-template-columns:repeat(8,minmax(0,1fr))}}
}`

  HTML = `{#if state.loading}
<div class="hd-note"><ui-spinner size="lg"></ui-spinner></div>
{:else if state.error}
<div class="hd-note"><ui-empty icon="warning-circle" title="Could not load your habits" text={state.error}></ui-empty></div>
{:else}
<div class="hd">
{#if state.form}
<form class="hd-form ui-card pad-md" style="--tag: var(--color-avatar-{state.form.color})" onsubmit="fez.save(event)" novalidate>
<ui-input name="name" label={state.adding ? 'New habit' : 'Name'} value={state.form.name} placeholder="Run, read, no phone after 10" maxlength="60" error={state.errors.name}></ui-input>
<div class="hd-label">Icon</div>
<div class="hd-icons" role="radiogroup" aria-label="Icon">
{#each state.icons as entry}
<button type="button" class="hd-icon" class:on={entry.on} key={entry.icon} role="radio" aria-checked={entry.on ? 'true' : 'false'} title={entry.label} aria-label={entry.label} onclick="fez.pickIcon('{entry.icon}')"><ui-icon name={entry.icon} size="18"></ui-icon></button>
{/each}
</div>
<div class="hd-label">Color</div>
<div class="hd-colors" role="radiogroup" aria-label="Color">
{#each state.colors as entry}
<button type="button" class="hd-color" class:on={entry.on} key={entry.color} role="radio" aria-checked={entry.on ? 'true' : 'false'} aria-label="Color {entry.color}" style="--tag: var(--color-avatar-{entry.color})" onclick="fez.pickColor({entry.color})">{#if entry.on}<ui-icon name="check" size="14"></ui-icon>{/if}</button>
{/each}
</div>
<div class="hd-form-actions">
<ui-button size="sm" label="Cancel" onclick="fez.cancelEdit()"></ui-button>
<ui-button size="sm" type="submit" variant="primary" label={state.adding ? 'Add habit' : 'Save'} :loading="state.busy !== ''"></ui-button>
</div>
</form>
{/if}
<ul class="hd-list">
{#each state.active as habit}
<li class="hd-row" key={habit.ref} style="--tag: var(--color-avatar-{habit.color})">
<span class="hd-badge"><ui-icon name={habit.icon} size="16"></ui-icon></span>
<span class="hd-text">
<span class="hd-name">{habit.name}</span>
{#if habit.streak}<span class="hd-streak">{habit.streak}</span>{/if}
</span>
<span class="hd-tools">
<ui-button variant="ghost" size="sm" icon="arrow-up" title="Move up" aria-label="Move {habit.name} up" :disabled="habit.first" onclick="fez.nudge('{habit.ref}', -1)"></ui-button>
<ui-button variant="ghost" size="sm" icon="arrow-down" title="Move down" aria-label="Move {habit.name} down" :disabled="habit.last" onclick="fez.nudge('{habit.ref}', 1)"></ui-button>
<ui-button variant="ghost" size="sm" icon="pencil-simple" title="Edit" aria-label="Edit {habit.name}" onclick="fez.edit('{habit.ref}')"></ui-button>
<ui-button variant="ghost" size="sm" icon="archive" title="Archive: hide it, keep its days" aria-label="Archive {habit.name}" onclick="fez.archive('{habit.ref}', true)"></ui-button>
</span>
</li>
{:else}
<li class="hd-empty">No habits yet. Add one, then check it off each day in the week and day views and in Next up.</li>
{/each}
</ul>
{#if state.archived.length}
<div class="hd-group">Archived</div>
<ul class="hd-list">
{#each state.archived as habit}
<li class="hd-row archived" key={habit.ref} style="--tag: var(--color-avatar-{habit.color})">
<span class="hd-badge"><ui-icon name={habit.icon} size="16"></ui-icon></span>
<span class="hd-text"><span class="hd-name">{habit.name}</span></span>
<span class="hd-tools">
<ui-button variant="ghost" size="sm" icon="arrow-counter-clockwise" label="Show again" onclick="fez.archive('{habit.ref}', false)"></ui-button>
<ui-button variant="danger-ghost" size="sm" icon="trash" label={habit.confirming ? 'Delete for good' : ''} title="Delete it and its days" aria-label="Delete {habit.name}" onclick="fez.remove('{habit.ref}')"></ui-button>
</span>
</li>
{/each}
</ul>
{/if}
<div class="dialog-actions">
{#if !state.form}
<ui-button icon="plus" label="New habit" onclick="fez.edit('')"></ui-button>
{/if}
<ui-button variant="primary" label="Done" onclick="fez.done()"></ui-button>
</div>
</div>
{/if}`
};
}

Fez('calendar-habits-dialog', __fez_component_0);

export default __fez_component_0;
